import { Injectable, signal } from '@angular/core';

import { NewPasswordChallenge } from '../models/new-password-challenge.model';

/**
 * Temporary store for a pending NEW_PASSWORD_REQUIRED challenge.
 *
 * Storage strategy (per security requirements):
 *  - primary: in-memory signal;
 *  - fallback: sessionStorage (survives an accidental page refresh only, and is
 *    cleared automatically when the tab closes).
 *
 * The `challenge_session` is sensitive: it is NEVER written to localStorage or
 * logged, and it is cleared immediately on success or cancellation.
 */
@Injectable({ providedIn: 'root' })
export class NewPasswordChallengeStore {
  private static readonly SESSION_KEY = 'auth.newPasswordChallenge';

  private readonly challengeSignal = signal<NewPasswordChallenge | null>(this.loadFromSession());
  readonly challenge = this.challengeSignal.asReadonly();

  set(challenge: NewPasswordChallenge): void {
    this.challengeSignal.set(challenge);

    try {
      sessionStorage.setItem(NewPasswordChallengeStore.SESSION_KEY, JSON.stringify(challenge));
    } catch {
      // sessionStorage unavailable (private mode / disabled): in-memory is enough.
    }
  }

  get(): NewPasswordChallenge | null {
    return this.challengeSignal() ?? this.loadFromSession();
  }

  hasChallenge(): boolean {
    return Boolean(this.get()?.challengeSession?.trim());
  }

  clear(): void {
    this.challengeSignal.set(null);

    try {
      sessionStorage.removeItem(NewPasswordChallengeStore.SESSION_KEY);
    } catch {
      // Nothing to clean up.
    }
  }

  private loadFromSession(): NewPasswordChallenge | null {
    try {
      const raw = sessionStorage.getItem(NewPasswordChallengeStore.SESSION_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as NewPasswordChallenge;
      return parsed?.challengeSession ? parsed : null;
    } catch {
      return null;
    }
  }
}
